import { Alert, Button } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ResourceName, type Task } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { LaserReviewIcon } from "@/components/ui/mui/LaserReviewIcon";
import { isOpenDeductibleReviewTask, taskRecordPath } from "@/resources/tasks/utils/taskRecordLink";

/**
 * Whether a task gets the "Resolve deductible review" call to action: a
 * deductible-review task that is still open or in progress (a cancelled task
 * is not completed, but its review is not waiting on anyone).
 */
export const showsDeductibleReviewAction = (task?: Task): task is Task => isOpenDeductibleReviewTask(task);

/**
 * Deductible-review task call to action (Feature #3502, #4243): the work is done
 * on the claimant's Deductibles tab, so the button deep-links there with the
 * resolution dialog open. Shared by the task page and the task dialog. A user
 * without claimant write access can still open the review read-only, so the
 * button says "View" for them.
 */
export const DeductibleReviewTaskAction = ({ task, onNavigate }: { task: Task; onNavigate?: () => void }) => {
  const { canEdit, canCreate, isLoading } = useResourcePermissions(ResourceName.CLAIMANTS);
  if (!showsDeductibleReviewAction(task)) return null;
  const canResolve = isLoading || canEdit || canCreate;
  return (
    <Alert
      severity="warning"
      icon={<LaserReviewIcon size={22} />}
      sx={{ alignItems: "center" }}
      action={
        <Button
          variant="contained"
          color="warning"
          size="small"
          endIcon={<ChevronRightIcon />}
          href={`/#${taskRecordPath(task)}`}
          onClick={onNavigate}
          data-testid="task-resolve-deductible-review"
        >
          {canResolve ? "Resolve deductible review" : "View deductible review"}
        </Button>
      }
    >
      Stop-loss claims for this claimant are on hold until the deductible review is resolved. The review opens on the
      claimant&apos;s Deductibles tab.
    </Alert>
  );
};
